import React, { useState, useEffect } from 'react';
import { View, Pressable } from 'react-native';
import { Feather } from '@expo/vector-icons';

import CustomText from './CustomText';
import AppStyles from '../AppStyles';
import { formatRelativeTime } from '../util/format';
import i18n, { t } from '../i18n';

/**
 * Presentation only. The wording for each action lives in the reports catalog
 * under timeline.action.* — a label held here could not be translated.
 */
const ACTION_CONFIG = {
  status_change: { icon: 'refresh-cw', color: AppStyles.colorSet.info },
  assignment_change: { icon: 'user-plus', color: AppStyles.colorSet.brandPrimary },
  todo_add: { icon: 'plus-square', color: AppStyles.colorSet.success },
  todo_toggle: { icon: 'check-square', color: AppStyles.colorSet.success },
  todo_delete: { icon: 'trash-2', color: AppStyles.colorSet.error },
  attachment_upload: { icon: 'paperclip', color: AppStyles.colorSet.brandPrimary },
  attachment_delete: { icon: 'trash-2', color: AppStyles.colorSet.error },
  comment_add: { icon: 'message-circle', color: AppStyles.colorSet.info },
  comment_edit: { icon: 'edit-2', color: AppStyles.colorSet.info },
  comment_delete: { icon: 'trash-2', color: AppStyles.colorSet.error },
  escalation: { icon: 'arrow-up-circle', color: '#DC2626' },
  'de-escalation': { icon: 'arrow-down-circle', color: AppStyles.colorSet.success },
  'reminders-muted': { icon: 'bell-off', color: '#6b7280' },
  'reminders-unmuted': { icon: 'bell', color: AppStyles.colorSet.info },
};

/**
 * Build the human-readable line for one activity entry.
 *
 * Every branch resolves a whole sentence from the catalog rather than
 * concatenating a verb phrase after the actor's name: Spanish and English do not
 * order subject and verb the same way, and a sentence assembled from fragments
 * leaves a translator nothing to reorder.
 */
function getDescription(entry) {
  const actor = entry.actor || t('reports:timeline.unknownActor');
  const details = entry.details || {};
  const text = truncate(details.todoText, 40);

  switch (entry.type) {
    case 'status_change':
      return t('reports:timeline.statusChange', {
        actor,
        from: details.fromStatus || '?',
        to: details.toStatus || '?',
      });
    case 'assignment_change':
      return details.toAssignee
        ? t('reports:timeline.assigned', { actor, assignee: details.toAssignee })
        : t('reports:timeline.unassigned', { actor });
    case 'todo_add':
      return t('reports:timeline.todoAdd', { actor, text });
    case 'todo_toggle':
      return details.completed
        ? t('reports:timeline.todoCompleted', { actor, text })
        : t('reports:timeline.todoUncompleted', { actor, text });
    case 'todo_delete':
      return t('reports:timeline.todoDelete', { actor, text });
    case 'attachment_upload':
      return t('reports:timeline.attachmentUpload', {
        actor,
        filename: details.filename || t('reports:timeline.file'),
      });
    case 'attachment_delete':
      return t('reports:timeline.attachmentDelete', {
        actor,
        filename: details.filename || t('reports:timeline.file'),
      });
    case 'comment_add':
      return details.adminOnly
        ? t('reports:timeline.commentAddAdminOnly', { actor })
        : t('reports:timeline.commentAdd', { actor });
    case 'comment_edit':
      return t('reports:timeline.commentEdit', { actor });
    case 'comment_delete':
      return t('reports:timeline.commentDelete', { actor });
    case 'escalation':
      return t('reports:timeline.escalation', { actor, level: details.toLevel || '?' });
    case 'de-escalation': {
      const reasonKey = `reports:timeline.reason.${details.reason}`;
      const reason = i18n.exists(reasonKey)
        ? t(reasonKey, { status: details.newStatus || '?' })
        : details.reason;
      return t('reports:timeline.deEscalation', { level: details.fromLevel || '?', reason });
    }
    case 'reminders-muted':
      return t('reports:timeline.remindersMuted', { actor });
    case 'reminders-unmuted':
      return t('reports:timeline.remindersUnmuted', { actor });
    default: {
      const actionKey = `reports:timeline.action.${entry.type}`;
      const action = i18n.exists(actionKey) ? t(actionKey) : entry.type;
      return t('reports:timeline.fallback', { actor, action });
    }
  }
}

function truncate(str, maxLen) {
  if (!str) return '';
  return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
}

const INITIAL_DISPLAY_LIMIT = 20;

const ActivityTimeline = ({ activityLog = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Reset "show all" when viewing a different report's log
  useEffect(() => {
    setShowAll(false);
  }, [activityLog.length]);

  if (activityLog.length === 0) return null;

  const sorted = activityLog.slice().sort((a, b) => b.timestamp - a.timestamp);
  const hasMore = sorted.length > INITIAL_DISPLAY_LIMIT;
  const visible = showAll ? sorted : sorted.slice(0, INITIAL_DISPLAY_LIMIT);

  return (
    <View style={{ marginTop: 12, paddingHorizontal: 4 }}>
      <Pressable
        onPress={() => setExpanded(!expanded)}
        style={({ hovered, focused }) => [
          {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingVertical: 6,
            paddingHorizontal: 6,
            marginHorizontal: -6,
            borderRadius: 6,
            cursor: 'pointer',
          },
          (hovered || focused) && { backgroundColor: AppStyles.stateSet.hover },
        ]}
      >
        <Feather name="activity" size={14} color={AppStyles.colorSet.textMuted} />
        <CustomText font="medium" size={12} color={AppStyles.colorSet.textMuted}>
          {t('reports:timeline.header', { count: activityLog.length })}
        </CustomText>
        <Feather
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={14}
          color={AppStyles.colorSet.textMuted}
        />
      </Pressable>

      {expanded && (
        <View style={{ marginTop: 4, paddingLeft: 4 }}>
          {visible.map((entry, idx) => {
            const config = ACTION_CONFIG[entry.type] || { icon: 'circle', color: AppStyles.colorSet.textMuted };
            return (
              <View
                key={`${entry.timestamp}-${entry.type}-${idx}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 10,
                  paddingVertical: 6,
                  borderBottomWidth: idx < visible.length - 1 ? 1 : 0,
                  borderBottomColor: AppStyles.colorSet.borderLight || '#f0f0f0',
                }}
              >
                <Feather name={config.icon} size={14} color={config.color} style={{ marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  <CustomText font="regular" size={12} color={AppStyles.colorSet.textPrimary}>
                    {getDescription(entry)}
                  </CustomText>
                  <CustomText font="regular" size={10} color={AppStyles.colorSet.textMuted} style={{ marginTop: 2 }}>
                    {formatRelativeTime(entry.timestamp, 'timeline')}
                  </CustomText>
                </View>
              </View>
            );
          })}
          {hasMore && !showAll && (
            <Pressable
              onPress={() => setShowAll(true)}
              style={{ paddingVertical: 8, alignItems: 'center', cursor: 'pointer' }}
            >
              {({ hovered, focused }) => (
                <CustomText
                  font="medium"
                  size={12}
                  color={AppStyles.colorSet.info}
                  style={(hovered || focused) && { textDecorationLine: 'underline' }}
                >
                  {t('reports:timeline.showAll', { count: sorted.length })}
                </CustomText>
              )}
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

export default ActivityTimeline;
